import { commentFetch } from '~/services/fetch-comment'

import { mergeThreadInSection, parseSection } from '~/features-shared/sections/api/section-parser'
import type { SwymSection, ThreadInSection } from '~/features-shared/sections/interface/swym-section'

import type { Section, SectionThread } from '~/features-generic/left-panel/interfaces/left-panel'

import type { SwymResponse } from '~/interfaces/services-dto'

/**
 * Get sections list by category
 * @param category
 * @returns section list
 */
export function getSections(category: string): Promise<Section<SectionThread>[]> {
  return commentFetch
    .get(`/api/v2/sections/${category}`)
    .json<SwymResponse<SwymSection[]>>()
    .then((res) => {
      return res.result.map(parseSection)
    })
}

/**
 * Create swym section
 * @param config
 * @param config.name Name of section
 * @param config.category Category of section
 * @param config.position Position of section
 * @returns return swym section
 */
export function createSection({ name, category, position }: {
  name: string
  category: string
  position: number
}): Promise<Section<SectionThread>> {
  return commentFetch
    .post('/api/v2/sections', {
      json: {
        name,
        position,
        category,
      },
    })
    .json<SwymResponse<SwymSection>>()
    .then(res => parseSection(res.result))
}

/**
 * Rename swym section name
 * @param config
 * @param config.sectionId Id of section
 * @param config.name New name of section
 * @returns return updated swym section
 */
export function renameSection({ sectionId, name }: {
  sectionId: string
  name: string
}): Promise<Section<SectionThread>> {
  return commentFetch
    .patch(`/api/v2/sections/${sectionId}/rename`, {
      json: {
        name,
      },
    })
    .json<SwymResponse<SwymSection>>()
    .then(res => parseSection(res.result))
}

/**
 * Delete section
 * @param sectionId Id of section
 */
export function deleteSection(sectionId: string): Promise<void> {
  return commentFetch
    .delete(`/api/v2/sections/${sectionId}`)
    .json()
}

/**
 * Add thread in section
 * @param config
 * @param config.sectionId Id of section
 * @param config.subjectUri subject_uri
 * @param config.position Position of thread in section
 * @returns return thread details
 */
export function addThreadInSection({ sectionId, subjectUri, position }: {
  sectionId: string
  subjectUri: string
  position: number
}): Promise<Section<SectionThread>[]> {
  return commentFetch
    .post(`/api/v2/sections/${sectionId}/subjects/${subjectUri}`, {
      json: {
        position,
      },
    })
    .json<SwymResponse<SwymSection[]>>()
    .then(res => res.result.map(parseSection))
}

/**
 * Remove thread from section
 * @param config
 * @param config.sectionId Id of section
 * @param config.subjectUri subject_uri
 */
export function removeFromSection({ sectionId, subjectUri }: {
  sectionId: string
  subjectUri: string
}): Promise<void> {
  return commentFetch
    .delete(`/api/v2/sections/${sectionId}/subjects/${subjectUri}`)
    .json()
}

/**
 * Reorder section
 * @param config
 * @param config.sectionId Id of section
 * @param config.position position of section
 * @returns return swym section
 */
export function reorderSection({ sectionId, position }: {
  sectionId: string
  position: number
}): Promise<Section<SectionThread>> {
  return commentFetch
    .patch(`/api/v2/sections/${sectionId}/move`, {
      json: {
        position,
      },
    })
    .json<SwymResponse<SwymSection>>()
    .then(data => parseSection(data.result))
}
